"use client";

import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Star } from "lucide-react";
import { useTheme } from "next-themes";
import type {
  LatLngExpression,
  Map as LeafletMap,
  LayerGroup,
  Layer,
  Marker as LeafletMarker,
} from "leaflet"; // type-only import; safe server-side
import { useAnalytics } from "@/components/providers/analytics-provider";

interface BizNavItem {
  id: string;
  name: string;
  latitude?: number | null;
  longitude?: number | null;
  rating?: number | null;
  review_count: number;
  specialties: string[];
}

export function BusinessMapQuickNav({
  items,
  slug,
}: {
  items: BizNavItem[];
  slug: string;
}) {
  const { trackTyped } = useAnalytics();
  // Track hovered marker index for grow/tooltip
  const [hoveredIdx, setHoveredIdx] = React.useState<number | null>(null);
  const [tooltip, setTooltip] = React.useState<{
    idx: number;
    latlng: LatLngExpression;
    name: string;
  } | null>(null);
  // Persistently selected business (clicked from quick nav or marker)
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  // Filter out items with valid coordinates
  const markers = React.useMemo(
    () =>
      items.filter(
        (b) => typeof b.latitude === "number" && typeof b.longitude === "number"
      ),
    [items]
  );

  // Basic bounds & center calculation
  const bounds = React.useMemo(() => {
    if (!markers.length) return null;
    const lats = markers.map((m) => m.latitude as number);
    const lngs = markers.map((m) => m.longitude as number);
    return {
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats),
      minLng: Math.min(...lngs),
      maxLng: Math.max(...lngs),
    };
  }, [markers]);

  const center: LatLngExpression = React.useMemo(() => {
    if (!bounds) return [37.0902, -95.7129]; // USA fallback
    return [
      (bounds.minLat + bounds.maxLat) / 2,
      (bounds.minLng + bounds.maxLng) / 2,
    ];
  }, [bounds]);

  // Refs for Leaflet runtime objects (loaded dynamically)
  const mapContainerRef = React.useRef<HTMLDivElement | null>(null);
  type LeafletPatched = typeof import("leaflet") & {
    __PLASTER_MARKER_PATCHED__?: boolean;
  };
  const leafletModuleRef = React.useRef<LeafletPatched | null>(null);
  const mapRef = React.useRef<LeafletMap | null>(null);
  const markersLayerRef = React.useRef<LayerGroup | null>(null);
  const markerInstancesRef = React.useRef<LeafletMarker[]>([]);
  const [ready, setReady] = React.useState(false);
  const { theme } = useTheme();

  // Load Leaflet dynamically on client only
  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      if (leafletModuleRef.current) return;
      const mod = await import("leaflet");
      if (cancelled) return;
      const L = (mod.default ?? mod) as LeafletPatched; // handle both default/named export style
      leafletModuleRef.current = L;
      // Styles are bundled via globals.css; default marker patch not needed since we use custom SVG icons.
      setReady(true);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Initialize map after Leaflet is ready
  React.useEffect(() => {
    if (!ready) return;
    if (!mapContainerRef.current || mapRef.current) return;
    const L = leafletModuleRef.current!; // loaded due to ready flag
    const map = L.map(mapContainerRef.current, {
      center: center as [number, number],
      zoom: 13,
      attributionControl: false,
      zoomControl: false,
    });
    mapRef.current = map;
    markersLayerRef.current = L.layerGroup().addTo(map);
  }, [ready, center]);

  // Theme-aware tile layer switching (with dark mode lightening filter)
  React.useEffect(() => {
    if (!ready) return;
    const L = leafletModuleRef.current!;
    const map = mapRef.current!;
    if (!L || !map) return;
    map.eachLayer((layer: Layer) => {
      const anyLayer = layer as unknown as { options?: { pane?: string } };
      if (anyLayer.options?.pane === "tilePane") {
        map.removeLayer(layer);
      }
    });
    const light = L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        attribution: "© OSM contributors",
        minZoom: 2,
        maxZoom: 19,
      }
    );
    light.addTo(map);
  }, [theme, ready]);

  // Plot markers & fit bounds (only when markers change, NOT on hover)
  React.useEffect(() => {
    if (!ready) return;
    const L = leafletModuleRef.current!;
    const map = mapRef.current!;
    const layer = markersLayerRef.current!;
    if (!L || !map || !layer) return;
    layer.clearLayers();
    markerInstancesRef.current = [];
    if (!markers.length) return;
    const boundsLatLng: LatLngExpression[] = [];
    function numberedCircleSvg(num: number) {
      return `<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'><circle cx='14' cy='14' r='13' fill='#e3342f' stroke='white' stroke-width='2'/><text x='14' y='18' text-anchor='middle' font-size='15' font-family='Arial,Helvetica,sans-serif' font-weight='bold' fill='white'>${num}</text></svg>`;
    }
    markers.forEach((m, idx) => {
      const latlng: LatLngExpression = [
        m.latitude as number,
        m.longitude as number,
      ];
      boundsLatLng.push(latlng);
      const icon = L.icon({
        iconUrl: `data:image/svg+xml;utf8,${encodeURIComponent(
          numberedCircleSvg(idx + 1)
        )}`,
        iconSize: [28, 28],
        iconAnchor: [14, 28],
        popupAnchor: [0, -28],
        shadowUrl: undefined,
      });
      const marker = L.marker(latlng, { icon });
      marker.on("click", () => {
        scrollToBiz(m.id);
        trackTyped("applicators_map_pin_click", {
          business_id: m.id,
          index: idx + 1,
          city_slug: slug,
        });
        // Zoom into this marker without changing overall bounds drastically
        map.setView(latlng as [number, number], Math.max(map.getZoom(), 15), {
          animate: true,
        });
      });
      marker.on("mouseover", () => {
        setHoveredIdx(idx);
        setTooltip({ idx, latlng, name: m.name });
      });
      marker.on("mouseout", () => {
        setHoveredIdx(null);
        setTooltip(null);
      });
      marker.addTo(layer);
      markerInstancesRef.current.push(marker);
    });
    const fit = leafletModuleRef.current!.latLngBounds(boundsLatLng);
    map.fitBounds(fit.pad(0.08));
  }, [markers, ready, slug, trackTyped]);

  // Apply hover scaling to existing marker DOM elements without re-fitting bounds
  React.useEffect(() => {
    markerInstancesRef.current.forEach((mk, i) => {
      const el = mk.getElement();
      if (!el) return;
      // Capture current translate transform if not scaled
      if (!el.style.transform.includes("scale")) {
        el.dataset.originalTransform = el.style.transform;
      }
      const base = el.dataset.originalTransform || el.style.transform;
      el.style.transition = "transform 120ms ease";
      el.style.transform = i === hoveredIdx ? `${base} scale(1.35)` : base;
      el.style.zIndex = i === hoveredIdx ? "1000" : "400";
    });
  }, [hoveredIdx]);

  // Keep original transform dataset in sync on map movements so scaling uses fresh translation
  React.useEffect(() => {
    if (!ready) return;
    const map = mapRef.current;
    if (!map) return;
    const sync = () => {
      markerInstancesRef.current.forEach((mk) => {
        const el = mk.getElement();
        if (!el) return;
        // Only update if current transform is pure translate (no scale appended)
        if (!el.style.transform.includes("scale")) {
          el.dataset.originalTransform = el.style.transform;
        }
      });
    };
    map.on("move", sync);
    map.on("zoom", sync);
    return () => {
      map.off("move", sync);
      map.off("zoom", sync);
    };
  }, [ready]);

  function scrollToBiz(id: string) {
    const el = document.getElementById(`biz-${id}`);
    if (!el) return;
    // Dynamically measure header height (fallback 72px) and apply offset margin
    const header = document.querySelector("header");
    const headerHeight = header ? (header as HTMLElement).offsetHeight : 72;
    const margin = 12; // extra breathing room below header
    const rect = el.getBoundingClientRect();
    const targetY = rect.top + window.scrollY - headerHeight - margin;
    window.scrollTo({ top: targetY < 0 ? 0 : targetY, behavior: "smooth" });
    setSelectedId(id);
    // Temporary pulse ring for visual feedback on the card itself
    el.classList.add("ring", "ring-primary", "ring-offset-2");
    setTimeout(() => {
      el.classList.remove("ring", "ring-primary", "ring-offset-2");
    }, 1600);
  }

  return (
    <div className="grid gap-8 md:grid-cols-2 mb-12">
      <Card className="relative h-[420px] overflow-hidden bg-card border-border">
        <div
          ref={mapContainerRef}
          className="absolute inset-0 applicators-map-container"
        />
        {/* Tooltip overlay for marker hover */}
        {tooltip &&
          Array.isArray(tooltip.latlng) &&
          mapRef.current &&
          (() => {
            const latlngTuple = tooltip.latlng as [number, number];
            const pt = mapRef.current!.latLngToContainerPoint(latlngTuple);
            return (
              <div
                style={{
                  position: "absolute",
                  left: pt.x + 8,
                  top: pt.y - 30,
                  zIndex: 1200,
                  pointerEvents: "none",
                }}
                className="px-2 py-1 rounded bg-[#222]/90 text-white text-[11px] font-medium shadow-md border border-white/20"
              >
                {tooltip.name}
              </div>
            );
          })()}
        {!markers.length && ready && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground p-4 text-center">
            No coordinates available for this city.
          </div>
        )}
        <div className="absolute top-0 left-0 right-0 p-3 flex items-center justify-between z-10">
          <h3 className="text-sm font-medium tracking-wide bg-linear-to-b from-background/70 to-background/10 backdrop-blur-sm p-2 py-1 rounded-sm">
            City Map
          </h3>
          <p className="text-[10px] text-foreground bg-linear-to-b from-background/70 to-background/10 backdrop-blur-sm p-1 rounded-sm">
            {markers.length}/{items.length} mapped
          </p>
        </div>
      </Card>
      <Card className="h-[420px] p-0 overflow-hidden flex flex-col">
        <div className="p-3 flex items-center justify-between bg-linear-to-b from-background/70 to-background/10 backdrop-blur-sm">
          <h3 className="text-sm font-medium tracking-wide">
            Quick Navigation
          </h3>
          <p className="text-[10px] text-muted-foreground">Click to jump</p>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3">
          {items.map((b, idx) => (
            <button
              key={b.id}
              type="button"
              onClick={() => scrollToBiz(b.id)}
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
              className={`group w-full text-left border rounded-md p-3 transition-colors flex items-center gap-2
              ${hoveredIdx === idx ? "ring-2 ring-primary" : ""}
              ${
                selectedId === b.id
                  ? "border-primary bg-primary/10"
                  : "border-border/60 bg-card/40 hover:border-border hover:bg-card"
              }`}
            >
              {/* Numbered red circle to match marker */}
              <span
                className={`flex items-center justify-center h-6 w-6 rounded-full bg-[#e3342f] text-white font-bold text-xs border-2 border-white mr-2 transition-transform
                ${hoveredIdx === idx ? " scale-110" : ""}
                ${
                  selectedId === b.id ? " shadow-lg ring-2 ring-white/40" : ""
                }`}
                style={{ minWidth: 24 }}
              >
                {idx + 1}
              </span>
              <div className="flex-1">
                <div className="flex items-start justify-between mb-1">
                  <span
                    className={`font-medium text-sm tracking-tight
                    ${hoveredIdx === idx ? "text-primary" : ""}
                    ${
                      selectedId === b.id
                        ? "text-primary"
                        : "group-hover:text-primary"
                    }`}
                  >
                    {b.name}
                  </span>
                  {typeof b.rating === "number" && (
                    <span className="flex items-center text-xs text-muted-foreground">
                      <Star className="h-3 w-3 mr-1 fill-yellow-400 text-yellow-400" />
                      {b.rating.toFixed(1)}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {b.specialties.slice(0, 4).map((s) => (
                    <Badge
                      key={s}
                      variant="secondary"
                      className="text-[10px] px-2 py-0 leading-4"
                    >
                      {s}
                    </Badge>
                  ))}
                  {b.specialties.length === 0 && (
                    <span className="text-[10px] text-muted-foreground">
                      (No specialties yet)
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}
